import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Store as StoreIcon, Business as OutletIcon, ExpandMore, Check } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetStoresQuery } from "@/store/api/storeApi";
import { useGetOutletsQuery } from "@/store/api/outletApi";
import { setStoreAndOutlet, selectStoreAndOutlet } from "@/store/slices/storeSlice";
import { switchStore, switchOutlet } from "@/store/slices/authSlice";
import { invalidateAllApiCaches } from "@/store/utils/resetApiCache";
import { useHasAccess } from "@/hooks/useHasAccess";
import type { AppDispatch } from "@/store";

const StoreOutletSelector: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { storeId, storeName, outletId, outletName } = useSelector(selectStoreAndOutlet);

  const { hasCurrentStoreRole } = useHasAccess();
  const isOwner = hasCurrentStoreRole('OWNER');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const open = Boolean(anchorEl);
  const hasInitializedOutlet = useRef(false);

  // Fetch stores
  const { data: storesData, isLoading: loadingStores } = useGetStoresQuery({
    page: 1,
    limit: 100,
  });

  // Fetch outlets for selected store
  const { data: outletsData, isLoading: loadingOutlets } = useGetOutletsQuery(
    {
      storeId: storeId || "",
      page: 1,
      limit: 100,
    },
    {
      skip: !storeId,
    }
  );

  const stores = useMemo(() => storesData?.data || [], [storesData?.data]);
  const outlets = useMemo(() => outletsData?.data || [], [outletsData?.data]);

  // Auto-select first store and outlet if none selected
  useEffect(() => {
    if (!storeId && stores.length > 0) {
      const firstStore = stores[0];
      // We'll select the store first, then wait for outlets to load
      if (firstStore) {
        // Temporarily select store without outlet
        dispatch(
          setStoreAndOutlet({
            storeId: firstStore.id,
            storeName: firstStore.name,
            outletId: "",
            outletName: "",
          })
        );
      }
    }
  }, [stores, storeId, dispatch]);

  // Auto-select first outlet only on initial load (not after deliberate clear like deletion)
  useEffect(() => {
    if (storeId && !outletId && outlets.length > 0 && !isSwitching) {
      if (hasInitializedOutlet.current) return;
      const firstOutlet = outlets[0];
      // Verify outlet belongs to current store to prevent stale cache data
      if (firstOutlet && firstOutlet.storeId === storeId) {
        dispatch(
          setStoreAndOutlet({
            storeId,
            storeName: storeName || "",
            outletId: firstOutlet.id,
            outletName: firstOutlet.name,
          })
        );
      }
    }
    if (outletId) {
      hasInitializedOutlet.current = true;
    }
  }, [outlets, storeId, outletId, storeName, dispatch, isSwitching]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStoreSelect = async (store: any) => {
    handleClose();
    console.log('[StoreOutletSelector] Store Selected:', store.name);

    // Don't do optimistic update that clears outletId - it causes UI glitch
    // Wait for backend response which includes the new outlet

    setIsSwitching(true);
    hasInitializedOutlet.current = false;

    // Update Backend Session (AuthSlice)
    try {
        // Invalidate caches FIRST to clear stale outlet data before switching
        invalidateAllApiCaches(dispatch);

        const result = await dispatch(switchStore(store.id) as any).unwrap();

        // Sync the UI store/outlet state with what the Backend decided.
        if (result.currentStore) {
            dispatch(
              setStoreAndOutlet({
                storeId: result.currentStore.id,
                storeName: result.currentStore.name,
                outletId: result.currentOutlet?.id || "",
                outletName: result.currentOutlet?.name || "",
              })
            );
        }

        // Invalidate caches again to refetch data with new store context
        invalidateAllApiCaches(dispatch);
    } catch (err) {
        console.error('Failed to switch store:', err);
    } finally {
        setIsSwitching(false);
    }
  };

  const handleOutletSelect = async (outlet: any) => {
    console.log('[StoreOutletSelector] Outlet Selected:', outlet.name);
    handleClose();

    setIsSwitching(true);

    // Update Backend Session (AuthSlice)
    try {
        const result = await dispatch(switchOutlet(outlet.id) as any).unwrap();

        // Sync UI state with backend result
        if (storeId && storeName && result.currentOutlet) {
             dispatch(
                setStoreAndOutlet({
                  storeId,
                  storeName,
                  outletId: result.currentOutlet.id,
                  outletName: result.currentOutlet.name,
                })
             );
        }

        // Invalidate caches to refetch data with new outlet context
        invalidateAllApiCaches(dispatch);
    } catch (err) {
        console.error('Failed to switch outlet:', err);
    } finally {
        setIsSwitching(false);
    }
  };

  const handleManageStores = () => {
    handleClose();
    navigate("/settings");
  };

  const { t } = useTranslation();

  if (loadingStores) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mx: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">{t('common:app.loading')}</Typography>
      </Box>
    );
  }

  if (stores.length === 0) {
    return (
      <Box sx={{ mx: 2 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<StoreIcon />}
          onClick={handleManageStores}
        >
          {t('common:actions.create')} Store
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mx: { xs: 0.5, sm: 2 } }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={isSwitching}
        endIcon={isSwitching ? <CircularProgress size={16} color="inherit" /> : <ExpandMore />}
        size="small"
        sx={{
          textTransform: "none",
          borderColor: "divider",
          color: "text.primary",
          minWidth: { xs: "100%", sm: 140 },
          maxWidth: "none",
          px: 2,
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mr: 1, overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, maxWidth: "100%" }}>
            <StoreIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography variant="body2" fontWeight="medium" noWrap>
              {isSwitching ? t('common:storeSelector.switching') : (storeName || t('common:storeSelector.select_store'))}
            </Typography>
          </Box>
          {!isSwitching && outletName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, maxWidth: "100%" }}>
              <OutletIcon sx={{ fontSize: 14, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {outletName}
              </Typography>
            </Box>
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 300,
            maxHeight: 500,
          },
        }}
      >
        {/* Stores Section */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            {t('common:storeSelector.stores')}
          </Typography>
        </Box>

        {stores.map((store: any) => (
          <MenuItem
            key={store.id}
            onClick={() => handleStoreSelect(store)}
            selected={store.id === storeId}
          >
            <ListItemIcon>{store.id === storeId && <Check />}</ListItemIcon>
            <ListItemText
              primary={store.name}
              secondary={`${store._count?.outlets || 0} ${t('common:storeSelector.outlets')}`}
            />
            {store.id === storeId && <Chip label={t('common:storeSelector.active')} size="small" color="primary" />}
          </MenuItem>
        ))}

        <Divider sx={{ my: 1 }} />

        {/* Outlets Section */}
        {storeId && (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                {t('common:storeSelector.outlets_title')}
              </Typography>
            </Box>

            {loadingOutlets ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : outlets.length === 0 ? (
              <MenuItem disabled>
                <ListItemText secondary={t('common:storeSelector.no_outlets')} />
              </MenuItem>
            ) : (
              outlets.map((outlet: any) => (
                <MenuItem
                  key={outlet.id}
                  onClick={() => handleOutletSelect(outlet)}
                  selected={outlet.id === outletId}
                >
                  <ListItemIcon>{outlet.id === outletId && <Check />}</ListItemIcon>
                  <ListItemText primary={outlet.name} secondary={outlet.code || t('common:storeSelector.no_code')} />
                  {!outlet.isActive && <Chip label={t('common:storeSelector.inactive')} size="small" color="default" />}
                </MenuItem>
              ))
            )}

            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Manage Button - Only visible to store owners */}
        {isOwner && (
          <MenuItem onClick={handleManageStores}>
            <ListItemIcon>
              <StoreIcon />
            </ListItemIcon>
            <ListItemText primary={t('common:storeSelector.manage')} />
          </MenuItem>
        )}

      </Menu>
    </Box>
  );
};

export default StoreOutletSelector;
